import React, {useState, useRef, useCallback, useMemo, useEffect} from 'react';
import ErrorBlock from '../components/ErrorBlock.jsx';
import logo from '/src/assets/img/logo.png';
import unblock from '/src/assets/img/unlocked.svg';
import shield from '/src/assets/img/shield.svg';
import tv from '/src/assets/img/tv-monitor.svg';
import devices from '/src/assets/img/devices.svg';
import lightning from '/src/assets/img/lightning.svg';
import support from '/src/assets/img/customer-service.svg';
import check from '/src/assets/img/check.svg';
import {Gradient} from 'whatamesh';
import { apiActivateTrial } from '../api/backend.js';

const PLANS = [
  {code: '1d', label: '1 день', stars: 0, title: 'Пробный'},
  {code: '1m', label: '1 месяц', stars: 120, slug: 'https://t.me/$15XVO2RPwUr9FwAAn7_x_O8kKw0'},
  {code: '6m', label: '6 мес.', stars: 450, slug: 'https://t.me/$dXKTB399WEjdEwAACoyUaOYStIQ'},
]

function gaEvent(name, params = {}) {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
  } catch (e) {
  }
}


function FeatureSlider() {
  const slides = [
    {
      title: 'Доступ к любым сайтам',
      text: 'Обходи блокировки и открывай интернет без границ.',
      icon: <img src={unblock} alt="Доступ"/>
    },
    {
      title: 'Приватность',
      text: 'Никто не узнает, чем ты занимаешься онлайн, мы гарантируем.',
      icon: <img src={shield} alt="Приватность"/>
    },
    {
      title: 'Без рекламы YouTube',
      text: 'Смотри видео без отвлекающих роликов и баннеров.',
      icon: <img src={tv} alt="Без рекламы"/>
    },
    {
      title: 'На любом устройстве',
      text: 'Телефон, телевизор, ноутбук или планшет — всё работает в несколько кликов.',
      icon: <img src={devices} alt="Устройства"/>
    },
    {
      title: 'Максимальная скорость',
      text: 'Игры, стримы и загрузки без лагов, задержек и региональных ограничений.',
      icon: <img src={lightning} alt="Скорость"/>
    },
    {
      title: 'Дружелюбная поддержка',
      text: 'Что-то не получается? Мы всегда на связи для разрешения любых ситуаций.',
      icon: <img src={support} alt="Поддержка"/>
    }
  ];


  const [index, setIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const wrapRef = useRef(null);
  const startXRef = useRef(0);
  const widthRef = useRef(1);
  const animRef = useRef(null);

  useEffect(() => {
    const update = () => {
      widthRef.current = wrapRef.current?.offsetWidth || 1;
    };
    update();
    const ro = new ResizeObserver(update);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  const toSlide = useCallback((next) => {
    setIndex((prev) => clamp(next, 0, slides.length - 1));
    setDragX(0);
  }, [slides.length]);

  const onPointerDown = (e) => {
    setIsDragging(true);
    startXRef.current = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    setDragX(0);
    // отключим анимации во время перетаскивания
    wrapRef.current.style.setProperty('--snap', '0');
    cancelAnimationFrame(animRef.current);
  };

  const onPointerMove = (e) => {
    if (!isDragging) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    setDragX(x - startXRef.current);
  };

  const onPointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const threshold = widthRef.current * 0.18;
    if (dragX > threshold) toSlide(index - 1);
    else if (dragX < -threshold) toSlide(index + 1);
    else setDragX(0);

    wrapRef.current.style.setProperty('--snap', '1');
  };

  useEffect(() => {
    const el = wrapRef.current;
    const onMove = (e) => onPointerMove(e);
    const onUp = () => onPointerUp();

    el.addEventListener('mousemove', onMove, {passive: true});
    el.addEventListener('touchmove', onMove, {passive: true});
    window.addEventListener('mouseup', onUp, {passive: true});
    window.addEventListener('touchend', onUp, {passive: true});
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, [isDragging, dragX, index, toSlide]);

  const percentShift = (-index * 100) + (dragX / widthRef.current) * 100;

  return (
    <div className="ff-slider" ref={wrapRef}
         onMouseDown={onPointerDown}
         onTouchStart={onPointerDown}
         role="region" aria-label="Feature slider">
      <div className="ff-track" style={{transform: `translateX(${percentShift}%)`}}>
        {slides.map((s, i) => (
          <article className="ff-card" key={i} aria-roledescription="slide" aria-label={`${i + 1} of ${slides.length}`}>
            <div className="ff-icon">{s.icon}</div>
            <div className="ff-title">{s.title}</div>
            <div className="ff-sub">{s.text}</div>
          </article>
        ))}
      </div>

      <div className="ff-dots" role="tablist" aria-label="Pagination">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`ff-dot ${i === index ? 'is-active' : ''}`}
            aria-selected={i === index}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => toSlide(i)}
          />
        ))}
      </div>
    </div>
  );
}

export default function Welcome({expired, onPaid}) {
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [selected, setSelected] = useState('1m');
  const canvasRef = useRef(null);
  const initedRef = useRef(false);

  const tg = useMemo(() => (window.Telegram && window.Telegram.WebApp) ? window.Telegram.WebApp : null, []);
  useEffect(() => {
    if (tg) {
      tg.ready?.();
      tg.expand?.();
    }
  }, [tg]);
  useEffect(() => {
    if (expired && selected === '1d') {
      setSelected('1m');
    }
  }, [expired, selected]);
  useEffect(() => {
    if (!canvasRef.current) return;
    if (initedRef.current) return;

    const gradient = new Gradient();
    gradient.initGradient('#gradient-canvas');
    initedRef.current = true;
  }, []);

  useEffect(() => {
    const user = tg?.initDataUnsafe?.user || null;
    gaEvent('opened_mini_app', {
      source: 'telegram_mini_app',
      opened_at: new Date().toISOString(),
      tg_user_id: user?.id ?? null,
      tg_username: user?.username ?? null,
      tg_language: user?.language_code ?? null,
      tg_platform: tg?.platform ?? null,
      app_version: tg?.version ?? null,
      is_expired_view: !!expired,
    });
  }, [tg, expired]);


  const startPay = useCallback(async () => {
    // 1. Сразу определяем пользователя из Telegram
    const user = tg?.initDataUnsafe?.user || null;

    // 2. Находим выбранный тариф
    const plan = PLANS.find(p => p.code === selected);
    if (!plan) {
      setError('Выбери тариф');
      return;
    }

    // Сбрасываем старые ошибки и ставим статус загрузки
    setError(null);
    setPaying(true);

    // 3. Логика бесплатного триала
    if (plan.code === '1d') {
      try {
        // Теперь user доступен здесь!
        await apiActivateTrial(user?.id, user?.username);
        await onPaid?.();
      } catch (e) {
        setError(e.message);
        setPaying(false);
      }
      return;
    }

    // 4. Логика платной подписки (Stars)
    if (!tg || !tg.openInvoice) {
      setError('Telegram WebApp API недоступен');
      setPaying(false);
      return;
    }

    if (!plan.slug) {
      setError('Slug тарифа не задан');
      setPaying(false);
      return;
    }

    // Трекаем событие начала оплаты
    gaEvent('begin_checkout', {
      source: 'telegram_mini_app',
      plan_code: plan.code,
      plan_label: plan.label,
      stars: plan.stars,
      currency: 'XTR',
      value: plan.stars,
      tg_user_id: user?.id ?? null,
      tg_username: user?.username ?? null,
    });

    tg.openInvoice(plan.slug, async (status) => {
      if (status === 'paid') {
        try {
          const uiPlan = ({
            '1m': {price:'120', sub:'30 дней',    title:'Месяц'},
            '6m': {price:'450', sub:'6 месяцев',  title:'Полгода'},
          }[plan.code]);

          gaEvent('sell', {
            source: 'telegram_mini_app',
            plan_code: plan.code,
            plan_label: plan.label,
            stars: plan.stars,
            currency: 'XTR',
            value: plan.stars,
            ui_price_label: uiPlan?.price ?? null,
            ui_title: uiPlan?.title ?? null,
            ui_sub: uiPlan?.sub ?? null,
            paid_at: new Date().toISOString(),
            tg_user_id: user?.id ?? null,
            tg_username: user?.username ?? null,
          });
          await onPaid?.();
        } catch (e) {
          setError(String(e.message || e));
        } finally {
          setPaying(false);
        }
      } else {
        if (status !== 'cancelled') {
          setError('Оплата не завершена: ' + status);
        }
        setPaying(false);
      }
    });
  }, [tg, selected, onPaid]);

  return (

    <section className="welcome-box" style={{position: 'relative'}}>
      <canvas
        ref={canvasRef}
        id="gradient-canvas"
        style={{
          position: 'fixed',
          inset: 0,
          width: '100%',
          height: '100%',
          zIndex: -1
        }}
      />
      <div className={'welcome-id'}>
        <img className="welcome-logo" src={logo} alt=""/>
      </div>
      <h1>
        Свободный интернет <span className={'accent'}>на твоей стороне</span>
      </h1>
      {expired && <div className={'expired'}>
        <h2>Ваша подписка истекла</h2>
        <p>Для возобновления доступа к AltaVPN оплатите тариф ниже</p>
      </div>}
      {!expired && <FeatureSlider/>}


      <div className="plans">
        {[
          ...(!expired ? [{
            code: '1d',
            title: 'Пробный',
            price: '0',
            sub: '1 день (бесплатно)',
          }] : []),
          {
            code: '1m',
            title: 'Месяц',
            price: '120',
            sub: '30 дней',
          },
          {
            code: '6m',
            title: 'Полгода',
            price: '450',
            sub: '6 месяцев',
          },
        ].map((p) => (
          <label
            key={p.code}
            className={`plan ${selected === p.code ? 'is-active' : ''}`}
            onClick={() => setSelected(p.code)}
          >
            <input
              type="radio"
              name="plan"
              value={p.code}
              checked={selected === p.code}
              onChange={() => setSelected(p.code)}
              className="plan-input"
            />

            {p.best && (
              <span className="plan-badge" aria-hidden={!p.best}>
          <span className="badge-dot"><img className={'check-badge'} src={check}/></span>
          Лучшая цена!
        </span>
            )}

            <div className="plan-row">
              <div className="plan-title">{p.title}</div>
              <div className="plan-price">{p.price}</div>
            </div>

            <div className="plan-sub">
              {p.old ? <s className="plan-old">{p.old}</s> : p.sub}
            </div>
          </label>
        ))}
      </div>

      {error && <div style={{marginTop: 12}}><ErrorBlock error={error}/></div>}

      <div className="tariff-block" style={{marginTop: 12}}>
        <button onClick={startPay} disabled={paying} className="gradient-btn">
          {paying ? 'Ожидаем подтверждение…' : 'Получить доступ'}
        </button>
      </div>
    </section>
  );
}
